/**
 * Enlace «A bordo» para invitar a la pareja: `/a-bordo?c=<código>#k=<llave>`.
 * El código va al servidor; la llave del enlace (detrás de «#») no sale nunca
 * del aparato y abre la llave sellada del presupuesto.
 */

export type InvitacionPendiente = { codigo: string; llaveEnlace?: string };

export const RUTA_A_BORDO = '/a-bordo';
const CLAVE = 'rumbo-a-bordo';
const RE_CODIGO = /^[A-Za-z0-9_-]{43}$/;

export function enlaceInvitacion(
  origen: string,
  codigo: string,
  llaveEnlace?: string,
): string {
  const base = `${origen}${RUTA_A_BORDO}?c=${encodeURIComponent(codigo)}`;
  return llaveEnlace ? `${base}#k=${encodeURIComponent(llaveEnlace)}` : base;
}

/** La invitación de una dirección `/a-bordo?c=…#k=…` (o `null`). */
export function leerEnlace(direccion: {
  pathname: string;
  search: string;
  hash: string;
}): InvitacionPendiente | null {
  if (direccion.pathname.replace(/\/+$/, '') !== RUTA_A_BORDO) {
    return null;
  }
  const codigo = new URLSearchParams(direccion.search).get('c') ?? '';
  if (!RE_CODIGO.test(codigo)) {
    return null;
  }
  const llave =
    new URLSearchParams(direccion.hash.replace(/^#/, '')).get('k') ?? '';
  return llave ? { codigo, llaveEnlace: llave } : { codigo };
}

function almacen(): Storage | null {
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

/**
 * Al abrir el enlace se guarda la invitación (sobrevive a ir a Google y
 * volver) y se quita la llave de la barra de direcciones.
 */
export function capturarInvitacion(): InvitacionPendiente | null {
  const enlace = leerEnlace(window.location);
  if (enlace) {
    try {
      almacen()?.setItem(CLAVE, JSON.stringify(enlace));
    } catch {
      // Sin almacenamiento: vale para esta visita.
    }
    window.history.replaceState(null, '', '/');
    return enlace;
  }
  return leerPendiente();
}

export function leerPendiente(): InvitacionPendiente | null {
  try {
    const raw = almacen()?.getItem(CLAVE);
    if (!raw) {
      return null;
    }
    const valor: unknown = JSON.parse(raw);
    if (
      valor &&
      typeof valor === 'object' &&
      'codigo' in valor &&
      typeof valor.codigo === 'string' &&
      RE_CODIGO.test(valor.codigo)
    ) {
      const llave =
        'llaveEnlace' in valor && typeof valor.llaveEnlace === 'string'
          ? valor.llaveEnlace
          : undefined;
      return llave
        ? { codigo: valor.codigo, llaveEnlace: llave }
        : { codigo: valor.codigo };
    }
  } catch {
    // Valor roto: como si no hubiera.
  }
  return null;
}

export function olvidarPendiente() {
  try {
    almacen()?.removeItem(CLAVE);
  } catch {
    // Nada que olvidar.
  }
}

const CLAVE_ABRIR = 'rumbo-a-bordo-abrir';

/**
 * Presupuesto al que se acaba de subir: cerrar el que estaba abierto puede
 * recargar la app, y al volver se abre este.
 */
export function guardarParaAbrir(fileId: string) {
  try {
    almacen()?.setItem(CLAVE_ABRIR, fileId);
  } catch {
    // Sin almacenamiento: se abre en esta misma visita.
  }
}

/** El presupuesto pendiente de abrir (y se olvida al leerlo). */
export function tomarParaAbrir(): string | null {
  try {
    const fileId = almacen()?.getItem(CLAVE_ABRIR) ?? null;
    almacen()?.removeItem(CLAVE_ABRIR);
    return fileId;
  } catch {
    return null;
  }
}
